import { FormattedDate, FormattedMessage } from "react-intl";
import { generatePath, Link, ShouldRevalidateFunction } from "react-router";
import Outlet from "@/common/Outlet";
import { MetadataItem } from "@/components/DatasetVersionCard";
import { Divided, DividedItem } from "@/components/Divided";
import EntityProfilePic from "@/components/EntityProfilePic";
import TagList from "@/components/TagList";
import WorkspaceMetadataEditButton from "@/components/WorkspaceMetadataEditButton";
import WorkspaceVersionCardActions from "@/components/WorkspaceVersionCardActions";
import { CalendarIcon, ChevronRightIcon, File04Icon } from "@/icons";
import { AsideContent, AsideRoot, AsideSection } from "@/kit/ui/aside";
import { Badge } from "@/kit/ui/badge";
import WorkspaceVersionHistory from "@/components/WorkspaceVersionHistory";
import { splitWorkspaceSlug } from "@/utils/routing";
import { Environment, graphql, useFragment } from "react-relay";
import { safeParse } from "valibot";
import { preload, usePreloaded } from "@/utils/relay";
import { LoaderArgs } from "@/utils/route";
import {
  WorkspacePageParams,
  WorkspaceVersionPageParams,
} from "@/utils/workspace";
import { WorkspaceAsideQuery as WorkspaceAsideQueryType } from "./__generated__/WorkspaceAsideQuery.graphql";
import { WorkspaceAsideVersionQuery as WorkspaceAsideVersionQueryType } from "./__generated__/WorkspaceAsideVersionQuery.graphql";
import { WorkspaceAsideFragment$key } from "./__generated__/WorkspaceAsideFragment.graphql";
export default function WorkspaceAside() {
  return (
    <AsideRoot>
      <AsideContent>
        <VersionSelector />
        <MenuHeader />
        <Menu />
      </AsideContent>

      <AsideSection>
        <Outlet />
      </AsideSection>
    </AsideRoot>
  );
}
function MenuHeader() {
  const { workspace } = useWorkspaceAsideQuery();
  if (!workspace) return null;
  return (
    <div className="flex items-center justify-between">
      <p className="text-base font-semibold text-grey-900 leading-normal">
        <FormattedMessage defaultMessage="Workspace Card" />
      </p>

      <WorkspaceMetadataEditButton workspace={workspace} />
    </div>
  );
}
function Menu() {
  const { workspace } = useWorkspaceAsideQuery();
  if (!workspace) return null;
  return (
    <Divided>
      <DividedItem
        title={<FormattedMessage defaultMessage="Short description" />}
        description={
          <FormattedMessage defaultMessage="Describe what your workspace is about in a few words." />
        }
      >
        {workspace.shortDescription && (
          <p className="text-grey-800 font-normal text-sm leading-tight">
            {workspace.shortDescription}
          </p>
        )}
      </DividedItem>

      <DividedItem
        title={<FormattedMessage defaultMessage="Categories" />}
        description={
          <FormattedMessage defaultMessage="Add categories to make your workspace easier to find." />
        }
      >
        <TagList taggable={workspace} />
      </DividedItem>

      <DividedItem
        title={<FormattedMessage defaultMessage="Licenses" />}
        description={
          <FormattedMessage defaultMessage="Let others know how they can use your workspace." />
        }
      >
        {workspace.license && (
          <div className="flex flex-row items-center gap-2">
            <File04Icon className="text-grey-600 w-5 h-5" />
            <p className="text-grey-600 font-normal text-sm leading-tight">
              {workspace.license}
            </p>
          </div>
        )}
      </DividedItem>
    </Divided>
  );
}
function VersionSelector() {
  const { workspace, version } = useWorkspaceAsideQuery();
  if (!workspace || !version) return null;
  // Show clone provenance only when the source is a *different* workspace;
  // every version also records its same-workspace predecessor, which we hide.
  const clonedFrom =
    version.clonedFrom && version.clonedFrom.workspace.id !== workspace.id
      ? version.clonedFrom
      : null;
  return (
    <div className="flex flex-col items-start justify-between bg-grey-25 border border-grey-200 rounded-xl overflow-hidden">
      <WorkspaceVersionHistory
        workspace={workspace}
        currentVersion={version?.version}
      >
        <button
          type="button"
          className="w-full flex flex-row items-center justify-between pl-3 pr-2 py-2 bg-linear-to-b from-white to-[#F9F8FC] border-b border-grey-200 group hover:cursor-pointer transition-colors hover:to-[#F8F7FF] focus:outline focus:outline-offset-1 focus:outline-brand-100"
        >
          <p className="text-base font-semibold text-grey-900 leading-normal transition-colors group-hover:text-brand-500">
            <FormattedMessage
              defaultMessage="Version: v{version}"
              values={{ version: version.version }}
            />
          </p>
          <ChevronRightIcon className="w-5 h-5 text-grey-700 transition-colors group-hover:text-brand-500" />
        </button>
      </WorkspaceVersionHistory>

      <div className="w-full flex flex-col items-start px-3 pt-4 pb-3 gap-y-3">
        <MetadataItem
          title={<FormattedMessage defaultMessage="Created by:" />}
          value={
            <Link
              to={generatePath("/:username", {
                username: version.entity.username,
              })}
            >
              <Badge hasPrefix shape="pill" className="gap-1">
                <EntityProfilePic entity={version.entity} size="4" />
                {version.entity.username}
              </Badge>
            </Link>
          }
          text={false}
        />
        {version.publishedAt ? (
          <MetadataItem
            title={<FormattedMessage defaultMessage="Published:" />}
            icon={CalendarIcon}
            value={
              <FormattedDate
                value={version.publishedAt}
                month="short"
                day="numeric"
                year="numeric"
              />
            }
          />
        ) : (
          <MetadataItem
            title={<FormattedMessage defaultMessage="Created on: " />}
            icon={CalendarIcon}
            value={
              <FormattedDate
                value={version.createdAt}
                month="short"
                day="numeric"
                year="numeric"
              />
            }
          />
        )}
        {clonedFrom && (
          <MetadataItem
            title={<FormattedMessage defaultMessage="Cloned from:" />}
            value={
              <Link
                to={generatePath("/:owner/:slug/versions/:version", {
                  ...splitWorkspaceSlug(clonedFrom.workspace.slug),
                  version: clonedFrom.version,
                })}
              >
                <Badge hasPrefix shape="pill" className="gap-1">
                  <FormattedMessage
                    defaultMessage="{slug} v{version}"
                    values={{
                      slug: clonedFrom.workspace.slug,
                      version: clonedFrom.version,
                    }}
                  />
                </Badge>
              </Link>
            }
            text={false}
          />
        )}
        {version.fromCompetition && (
          <MetadataItem
            title={<FormattedMessage defaultMessage="Track:" />}
            value={
              <Link
                to={generatePath("/challenges/:owner/tracks/:slug", {
                  owner:
                    version.fromCompetition.events.nodes[0]?.event.slug ?? "",
                  slug: version.fromCompetition.slug,
                })}
              >
                <Badge hasPrefix shape="pill" className="gap-1">
                  {version.fromCompetition.title}
                </Badge>
              </Link>
            }
            text={false}
          />
        )}
        <WorkspaceVersionCardActions version={version} />
      </div>
    </div>
  );
}
const WorkspaceFragment = graphql`
  fragment WorkspaceAsideFragment on Workspace {
    id
    shortDescription
    license
    createdAt
    versions(first: 1) @connection(key: "WorkspaceAsideFragment_versions") {
      edges {
        node {
          id
          entity {
            username
            ...EntityProfilePicFragment @arguments(thumbnail: true)
          }
          createdAt
          version
          publishedAt
          clonedFrom @ifAllowed {
            id
            version
            workspace {
              id
              slug
            }
          }
          fromCompetition @ifAllowed {
            id
            title
            slug
            events(first: 1) {
              nodes {
                event {
                  slug
                }
              }
            }
          }
          ...WorkspaceVersionCardActionsFragment
        }
      }
    }
    ...TagListFragment
    ...WorkspaceMetadataEditButtonFragment
    ...WorkspaceVersionHistoryFragment
  }
`;
const WorkspaceAsideQuery = graphql`
  query WorkspaceAsideQuery($owner: String!, $slug: String!) {
    workspaceBySlug(owner: $owner, slug: $slug) {
      ...WorkspaceAsideFragment
    }
  }
`;
const WorkspaceAsideVersionQuery = graphql`
  query WorkspaceAsideVersionQuery(
    $owner: String!
    $slug: String!
    $version: Semver!
  ) {
    workspaceBySlug(owner: $owner, slug: $slug) {
      ...WorkspaceAsideFragment
      version(version: $version) {
        id
        entity {
          username
          ...EntityProfilePicFragment @arguments(thumbnail: true)
        }
        createdAt
        version
        publishedAt
        clonedFrom @ifAllowed {
          id
          version
          workspace {
            id
            slug
          }
        }
        fromCompetition @ifAllowed {
          id
          title
          slug
          events(first: 1) {
            nodes {
              event {
                slug
              }
            }
          }
        }
        ...WorkspaceVersionCardActionsFragment
      }
    }
  }
`;
export const loader =
  (environment: Environment) =>
  ({ params }: LoaderArgs) => {
    let res;
    res = safeParse(WorkspaceVersionPageParams, params);
    if (res.success) {
      return preload<WorkspaceAsideVersionQueryType>(
        environment,
        WorkspaceAsideVersionQuery,
        res.output,
      );
    }
    res = safeParse(WorkspacePageParams, params);
    if (res.success) {
      return preload<WorkspaceAsideQueryType>(
        environment,
        WorkspaceAsideQuery,
        res.output,
      );
    }
    throw new Error("unexpected WorkspaceAside params");
  };
const useWorkspaceAsideQuery = () => {
  const { query, ...preloaded } = usePreloaded<
    WorkspaceAsideQueryType | WorkspaceAsideVersionQueryType
  >();
  const workspace = useFragment(
    WorkspaceFragment,
    query.workspaceBySlug as WorkspaceAsideFragment$key | null,
  );
  let version = null;
  if (query.workspaceBySlug && "version" in query.workspaceBySlug) {
    version = query.workspaceBySlug.version;
  } else {
    version = workspace?.versions.edges[0]?.node;
  }
  return {
    query,
    workspace,
    version,
    ...preloaded,
  };
};

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  currentUrl,
  nextUrl,
}) => defaultShouldRevalidate || currentUrl.pathname !== nextUrl.pathname;
